import { Store } from '../state/store';

const SCROLL_POSITIONS_KEY = 'chat_scroll_positions';

export class ScrollManager {
    private store: Store;
    private scrollToBottomBtn: HTMLElement | null = null;
    private streamingRequiredDocHeight: number | null = null;
    private scrollPositions: Map<string, number> = new Map();

    constructor(store: Store) {
        this.store = store;
        this.loadScrollPositions();
    }

    private loadScrollPositions(): void {
        try {
            const saved = sessionStorage.getItem(SCROLL_POSITIONS_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                this.scrollPositions = new Map(Object.entries(parsed).map(([k, v]) => [k, v as number]));
            }
        } catch {
            this.scrollPositions = new Map();
        }
    }

    private saveScrollPositions(): void {
        try {
            const obj = Object.fromEntries(this.scrollPositions);
            sessionStorage.setItem(SCROLL_POSITIONS_KEY, JSON.stringify(obj));
        } catch {
            
        }
    }

    saveScrollPosition(conversationId: string): void {
        if (!conversationId) return;
        this.scrollPositions.set(conversationId, window.scrollY);
        this.saveScrollPositions();
    }

    getScrollPosition(conversationId: string): number | null {
        return this.scrollPositions.get(conversationId) ?? null;
    }

    clearScrollPosition(conversationId: string): void {
        this.scrollPositions.delete(conversationId);
        this.saveScrollPositions();
    }

    setup(): void {
        this.scrollToBottomBtn = document.getElementById('scroll-to-bottom');
        this.bindEvents();
    }

    private bindEvents(): void {
        window.addEventListener('scroll', () => this.handleScroll());

        this.scrollToBottomBtn?.addEventListener('click', () => {
            this.scrollToBottom('smooth');
            this.store.setUserScrolledUp(false);
            this.store.setAutoScroll(true);
        });

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                const conversationId = this.store.getCurrentConversationId();
                if (conversationId) {
                    this.saveScrollPosition(conversationId);
                }
            }
        });
    }

    private handleScroll(): void {
        const scrollPosition = window.scrollY + window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        const distanceFromBottom = documentHeight - scrollPosition;
        const threshold = 50;
        const isNearBottom = distanceFromBottom < threshold;

        const wasUserScrolledUp = this.store.getUserScrolledUp();
        const isUserScrolledUp = !isNearBottom;

        if (wasUserScrolledUp !== isUserScrolledUp) {
            this.store.setUserScrolledUp(isUserScrolledUp);
            this.store.setAutoScroll(isNearBottom);

            if (this.scrollToBottomBtn) {
                if (isUserScrolledUp) {
                    this.scrollToBottomBtn.classList.remove('hidden');
                } else {
                    this.scrollToBottomBtn.classList.add('hidden');
                }
            }
        }
    }

    isScrolledToBottom(): boolean {
        const scrollPosition = window.scrollY + window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        const threshold = 100;
        return documentHeight - scrollPosition < threshold;
    }

    getScrollDistanceFromBottom(): number {
        const scrollPosition = window.scrollY + window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        return documentHeight - scrollPosition;
    }

    scrollTo(position: number, behavior: ScrollBehavior = 'smooth'): void {
        window.scrollTo({
            top: position,
            behavior: behavior
        });
    }

    scrollToBottom(behavior: ScrollBehavior = 'smooth'): void {
        const documentHeight = document.documentElement.scrollHeight;
        this.scrollTo(documentHeight, behavior);
    }

    scrollToTop(): void {
        window.scrollTo({ top: 0, behavior: 'auto' });
    }

    updateButtonPosition(): void {
        const inputAreaWrapper = document.querySelector('.input-area-wrapper') as HTMLElement;
        
        if (!this.scrollToBottomBtn || !inputAreaWrapper) return;
        
        const inputAreaHeight = inputAreaWrapper.offsetHeight;
        this.scrollToBottomBtn.style.bottom = `${inputAreaHeight}px`;
    }

    checkScrollContainerState(): void {
        if (!this.scrollToBottomBtn) return;

        const documentHeight = document.documentElement.scrollHeight;
        const viewportHeight = window.innerHeight;
        const isScrollable = documentHeight > viewportHeight;

        if (isScrollable) {
            this.scrollToBottomBtn.classList.add('hidden');
            this.store.setUserScrolledUp(false);
            this.store.setAutoScroll(true);
        } else {
            this.scrollToBottomBtn.classList.add('hidden');
        }
    }

    expandAndScrollToExchange(assistantMessageId: string): void {
        const messagesList = document.getElementById('messages-list') as HTMLElement;
        const assistantEl = document.querySelector(`.message[data-id="${assistantMessageId}"]`) as HTMLElement;
        const headerEl = document.querySelector(`header`) as HTMLElement;

        if (!messagesList || !assistantEl) return;

        const allMessages = Array.from(document.querySelectorAll('.message'));
        const assistantIndex = allMessages.findIndex(el => el.getAttribute('data-id') === assistantMessageId);
        const prevUserMessage = [...allMessages.slice(0, assistantIndex)].reverse().find(el =>
            el.classList.contains('message-user')
        ) as HTMLElement;

        if (!prevUserMessage) return;

        const headerHeight = headerEl?.offsetHeight || 64;
        const viewportHeight = window.innerHeight;
        const userMessageHeight = prevUserMessage.offsetHeight;
        
        const getOffsetDocTop = (el: HTMLElement): number => {
            let top = 0;
            let current: HTMLElement | null = el;
            while (current) {
                top += current.offsetTop;
                current = current.offsetParent as HTMLElement | null;
            }
            return top;
        };
        
        const userMsgDocTop = getOffsetDocTop(prevUserMessage);
        const assistantDocBottom = getOffsetDocTop(assistantEl) + assistantEl.offsetHeight;

        const maxContextHeight = viewportHeight * 0.25;
        let targetScrollTop: number;
        
        if (userMessageHeight <= maxContextHeight) {
            targetScrollTop = userMsgDocTop - headerHeight;
        } else {
            targetScrollTop = userMsgDocTop + userMessageHeight - maxContextHeight - headerHeight;
        }

        const inputWrapper = document.querySelector('.input-area-wrapper') as HTMLElement;
        const inputWrapperHeight = inputWrapper?.offsetHeight || 0;
        const containerEl = messagesList.parentElement as HTMLElement;
        const containerPaddingBottom = parseFloat(getComputedStyle(containerEl).paddingBottom) || 0;

        const requiredDocHeight = targetScrollTop + viewportHeight - inputWrapperHeight - containerPaddingBottom;
        const paddingNeeded = Math.floor(Math.max(0, requiredDocHeight - assistantDocBottom));
        
        this.streamingRequiredDocHeight = requiredDocHeight;
        messagesList.style.paddingBottom = `${paddingNeeded}px`;

        this.scrollTo(targetScrollTop, 'smooth');
    }

    updatePaddingDuringStreaming(assistantMessageId: string): void {
        if (this.streamingRequiredDocHeight === null) return;

        const messagesList = document.getElementById('messages-list') as HTMLElement;
        const assistantEl = document.querySelector(`.message[data-id="${assistantMessageId}"]`) as HTMLElement;

        if (!messagesList || !assistantEl) return;

        const currentScrollY = window.scrollY;
        const assistantRect = assistantEl.getBoundingClientRect();
        const assistantDocBottom = currentScrollY + assistantRect.bottom;
        
        const getOffsetDocTop = (el: HTMLElement): number => {
            let top = 0;
            let current: HTMLElement | null = el;
            while (current) {
                top += current.offsetTop;
                current = current.offsetParent as HTMLElement | null;
            }
            return top;
        };
        
        const assistantOffsetDocTop = getOffsetDocTop(assistantEl);
        const assistantOffsetHeight = assistantEl.offsetHeight;

        const paddingNeeded = Math.floor(Math.max(0, this.streamingRequiredDocHeight - assistantDocBottom));
        messagesList.style.paddingBottom = `${paddingNeeded}px`;
    }

    /**
     * Clears the streaming padding from the messages-list.
     * 
     * IMPORTANT: Only call this when switching conversations, NOT when streaming completes.
     * The padding must remain after streaming to maintain the expanded viewport for the
     * user-assistant exchange. Clearing it would break the expansion feature.
     */
    clearStreamingPadding(): void {
        const messagesList = document.getElementById('messages-list') as HTMLElement;
        if (messagesList) {
            messagesList.style.paddingBottom = '0px';
        }
        this.streamingRequiredDocHeight = null;
    }
}